import React, { useMemo, useEffect } from 'react';
import { useImmer } from 'use-immer';
import type * as H from 'history';
import { createContext } from '@shared/utils/createContext';
// Importing directly from 'react-router-dom' since `src/shared/core/router.helper/reactRouter.tsx` consumes
// the FeatureFlagsProvider context

// eslint-disable-next-line no-restricted-imports
import { useHistory, useLocation } from 'react-router-dom';
import type {
  FeatureFlagsProviderProps,
  FeatureFlagsState,
  FeatureFlagsContextType,
  FeatureFlagKey,
  BooleanStringBasedFeatureKeys,
  StringBasedFeatureKeys,
  RemoteFeatureFlagKeys,
  RemoteFeatureFlagOptions,
  SetUserConsentHandler,
  ConsentFeatureFlagKey,
} from './featureFlags.types';
import type { EventFeatureFlagOptions } from './eventFeatureFlags';
import { useEventFeatureFlagsContext } from './eventFeatureFlags';
import { deriveFeatureContextFromState, updateStateDraftFromLocation, createFeatureFlagsUrlFormatter } from './featureFlags.utils';
import { FEATURE_FLAG_VALUES } from './featureFlags.constants';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { RemoteFeatureFlag } from './remoteFeatureFlags';
import { RemoteFeatureFlagsProvider, useRemoteFeatureFlag } from './remoteFeatureFlags';
import { useUserConsent } from '@shared/utils/userConsent';

const [Provider, useFeatureFlagsContext, InternalFeatureFlagsContext] = createContext<FeatureFlagsContextType>({ name: 'FeatureFlagQueryStrings' });

/**
 * `state` only changes when a feature flag query param changes, so a formatToUrl cache can outlive
 * many navigations. Bound it — it only has to cover the links rendered at one time.
 *
 * The bound must exceed the distinct link count of the busiest page, or the FIFO eviction below
 * degenerates: a render pass walks the same urls in the same order, so every lookup evicts the
 * entry the next pass needs and the hit rate collapses to ~0. The admin registry shop renders 357
 * distinct urls, which thrashed the previous 256.
 */
export const FORMAT_TO_URL_CACHE_LIMIT = 528;

const FeatureFlagsProvider = ({ children, config = FEATURE_FLAG_VALUES, initialAmplitudeFeatureFlagVariants }: FeatureFlagsProviderProps) => {
  const location = useLocation();
  const history = useHistory();

  const [state, setState] = useImmer<FeatureFlagsState>(() => {
    const stateDraft = Object.entries(config).reduce((acc, [key]) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      acc[key] = undefined;
      return acc;
    }, {} as FeatureFlagsState);

    updateStateDraftFromLocation(config, stateDraft, location);
    return stateDraft;
  });

  const handleOnHistoryChange = useEventCallback<H.LocationListener>(location => {
    setState(draft => {
      updateStateDraftFromLocation(config, draft, location);
    });
  });

  useEffect(() => {
    const unsubscribe = history.listen(handleOnHistoryChange);
    return () => {
      unsubscribe();
    };
  }, [history, handleOnHistoryChange]);

  const value = useMemo<FeatureFlagsContextType>(() => {
    // Every <Link> render formats its target through here, and the app renders the same
    // handful of urls hundreds of times.
    const urlCache = new Map<string, string>();
    const formatUrl = createFeatureFlagsUrlFormatter(config, state);
    return {
      features: deriveFeatureContextFromState(config, state),
      formatToUrl: (url = '') => {
        const cached = urlCache.get(url);
        if (cached !== undefined) {
          return cached;
        }
        const formatted = formatUrl(url);
        if (urlCache.size >= FORMAT_TO_URL_CACHE_LIMIT) {
          urlCache.delete(urlCache.keys().next().value as string);
        }
        urlCache.set(url, formatted);
        return formatted;
      },
    } as FeatureFlagsContextType;
  }, [state, config]);

  return (
    <Provider value={value}>
      <RemoteFeatureFlagsProvider initialAmplitudeFeatureFlagVariants={initialAmplitudeFeatureFlagVariants}>{children}</RemoteFeatureFlagsProvider>
    </Provider>
  );
};

function useFeatureValue<Key extends RemoteFeatureFlagKeys>(featureName: Key, options?: RemoteFeatureFlagOptions): RemoteFeatureFlag<Key>;
function useFeatureValue<Key extends StringBasedFeatureKeys>(featureName: Key): string | undefined;
function useFeatureValue<Key extends BooleanStringBasedFeatureKeys>(featureName: Key): boolean;
function useFeatureValue<Key extends FeatureFlagKey>(featureName: Key, options?: RemoteFeatureFlagOptions) {
  const { features } = useFeatureFlagsContext();
  const feature = features[featureName];

  const isRemoteFeatureFlag = feature.type === 'remoteconfig';
  const remoteFeatureFlagValue = useRemoteFeatureFlag(isRemoteFeatureFlag ? (featureName as RemoteFeatureFlagKeys) : undefined, options);
  if (isRemoteFeatureFlag) {
    return remoteFeatureFlagValue;
  }

  const stateOrDefaultValue = feature.value ?? feature.defaultValue;

  // always return false for boolean feature flags if state/default value is undefined
  return feature.type === 'boolean' ? (stateOrDefaultValue ?? false) : stateOrDefaultValue;
}

function useIsFeatureEnabled(
  options: RemoteFeatureFlagOptions & { consentKey: ConsentFeatureFlagKey },
): [isEnabled: boolean, isLoading: boolean, setUserConsentHandler: SetUserConsentHandler, payload: unknown];
function useIsFeatureEnabled(options: EventFeatureFlagOptions): [isEnabled: boolean, isLoading: boolean];
function useIsFeatureEnabled<Key extends RemoteFeatureFlagKeys>(
  featureName: Key,
  options?: RemoteFeatureFlagOptions,
): [isEnabled: boolean, isLoading: boolean, setUserConsentHandler: SetUserConsentHandler, payload: unknown];
function useIsFeatureEnabled<Key extends StringBasedFeatureKeys>(
  featureName: Key,
): [isEnabled: boolean, isLoading: boolean, setUserConsentHandler: SetUserConsentHandler, payload: unknown];
function useIsFeatureEnabled<Key extends BooleanStringBasedFeatureKeys>(
  featureName: Key,
): [isEnabled: boolean, isLoading: boolean, setUserConsentHandler: SetUserConsentHandler, payload: unknown];
function useIsFeatureEnabled<Key extends FeatureFlagKey>(
  featureNameOrOptions: Key | (RemoteFeatureFlagOptions & { consentKey: ConsentFeatureFlagKey }) | EventFeatureFlagOptions,
  maybeOptions?: RemoteFeatureFlagOptions,
): // Consent mode
| [isEnabled: boolean, isLoading: boolean, setUserConsentHandler: SetUserConsentHandler, payload: unknown]
  // Event feature flag mode
  | [isEnabled: boolean, isLoading: boolean] {
  const { features } = useFeatureFlagsContext();
  const { getUserConsent, setUserConsent, loadingUserConsent } = useUserConsent();
  const { isEventFeatureFlagEnabled, loading: eventFlagsLoading } = useEventFeatureFlagsContext();

  const isConsentMode = typeof featureNameOrOptions === 'object' && 'consentKey' in featureNameOrOptions;
  const isEventFeatureFlagMode = typeof featureNameOrOptions === 'object' && 'eventFeatureFlagKey' in featureNameOrOptions;

  const featureName: FeatureFlagKey | undefined = isEventFeatureFlagMode ? undefined : isConsentMode ? featureNameOrOptions.consentKey : featureNameOrOptions;
  const options = isConsentMode ? featureNameOrOptions : maybeOptions || {};
  const feature = featureName ? features[featureName] : undefined;

  const isRemoteFeatureFlag = feature?.type === 'remoteconfig';
  const remoteFeatureFlagValue = useRemoteFeatureFlag(isRemoteFeatureFlag ? (featureName as RemoteFeatureFlagKeys) : undefined, options);

  const handleOnSetIsFeatureEnabled = async (isFeatureEnabled: boolean) => {
    if (isConsentMode) {
      return await setUserConsent(featureNameOrOptions.consentKey, isFeatureEnabled);
    }
    return;
  };

  // Handle event feature flag mode
  if (isEventFeatureFlagMode) {
    const { eventFeatureFlagKey, skip } = featureNameOrOptions;
    const isEnabled = skip ? false : isEventFeatureFlagEnabled(eventFeatureFlagKey);
    return [isEnabled, eventFlagsLoading];
  }

  const stateOrDefaultValue = feature ? (feature.value ?? feature.defaultValue) : undefined;

  if (isConsentMode && feature?.type === 'boolean') {
    const consentValue = getUserConsent(featureNameOrOptions.consentKey);
    if (consentValue === true || consentValue === false) {
      return [consentValue, loadingUserConsent, handleOnSetIsFeatureEnabled, undefined];
    }
  }

  let isEnabled = typeof stateOrDefaultValue == 'boolean' ? stateOrDefaultValue : stateOrDefaultValue === 'on' || stateOrDefaultValue === 'treatment';
  let isLoading = false;
  let payload = undefined;

  if (isRemoteFeatureFlag) {
    isEnabled = remoteFeatureFlagValue.value === 'on' || remoteFeatureFlagValue.value === 'treatment';
    isLoading = remoteFeatureFlagValue.loading;
    payload = remoteFeatureFlagValue.payload;
  } else if (isConsentMode && feature?.type === 'boolean') {
    isLoading = loadingUserConsent;
  }

  return [isEnabled, isLoading, handleOnSetIsFeatureEnabled, payload];
}

export { useFeatureValue, useIsFeatureEnabled, FeatureFlagsProvider, useFeatureFlagsContext, InternalFeatureFlagsContext };
